import React, { useState, useRef, useEffect } from 'react'

interface PieData {
  name: string
  value: number
  color: string
  field: string
}

interface DraggablePieChartProps {
  data: PieData[]
  onValueChange: (field: string, value: number) => void
  size?: number
  showHandles?: boolean
}

export function DraggablePieChart({ data, onValueChange, size = 320, showHandles = true }: DraggablePieChartProps) {
  const [dragging, setDragging] = useState<number | null>(null)
  const [initialValues, setInitialValues] = useState<number[]>([])
  const [startAngle, setStartAngle] = useState(0)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const centerX = size / 2
  const centerY = size / 2
  const radius = (size * 0.8) / 2
  const innerRadius = radius * 0.55

  const getAngleFromMouse = (clientX: number, clientY: number) => {
    if (!svgRef.current) return 0
    const rect = svgRef.current.getBoundingClientRect()
    const x = clientX - rect.left - centerX
    const y = clientY - rect.top - centerY
    let angle = (Math.atan2(y, x) * 180) / Math.PI
    angle = (angle + 360) % 360
    return angle
  }

  const handleMouseDown = (boundaryIndex: number) => (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragging(boundaryIndex)
    setInitialValues(data.map(d => d.value))
    setStartAngle(getAngleFromMouse(e.clientX, e.clientY))
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (dragging === null || !svgRef.current || initialValues.length === 0) return

    const currentAngle = getAngleFromMouse(e.clientX, e.clientY)
    
    // Calculate how much the mouse has moved from start
    let angleDelta = currentAngle - startAngle
    
    // Normalize to -180 to 180 for proper wrap-around
    while (angleDelta > 180) angleDelta -= 360
    while (angleDelta < -180) angleDelta += 360

    // Convert angle change to percentage change (360° = 100%)
    const percentDelta = (angleDelta / 360) * 100

    // The boundary is between segment[dragging] and segment[dragging+1]
    const segmentIndex = dragging
    const nextIndex = (dragging + 1) % data.length

    // Calculate new values based on INITIAL state (for smooth dragging)
    let newSegmentValue = initialValues[segmentIndex] + percentDelta
    let newNextValue = initialValues[nextIndex] - percentDelta

    // Clamp values to reasonable ranges
    newSegmentValue = Math.max(5, Math.min(90, newSegmentValue))
    newNextValue = Math.max(5, Math.min(90, newNextValue))

    // Calculate sum of segments we're NOT changing
    const otherSum = initialValues.reduce((sum, val, idx) => 
      (idx !== segmentIndex && idx !== nextIndex) ? sum + val : sum, 0
    )

    // Ensure total equals 100
    const total = newSegmentValue + newNextValue + otherSum
    
    // Only update if total is valid (allow 1% tolerance for rounding)
    if (Math.abs(total - 100) <= 1) {
      onValueChange(data[segmentIndex].field, Math.round(newSegmentValue))
      onValueChange(data[nextIndex].field, Math.round(newNextValue))
    }
  }

  const handleMouseUp = () => {
    setDragging(null)
  }

  useEffect(() => {
    const handleGlobalMouseUp = () => setDragging(null)
    if (dragging !== null) {
      document.addEventListener('mouseup', handleGlobalMouseUp)
      return () => document.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [dragging])

  // Calculate paths
  const paths: Array<{
    path: string
    color: string
    startAngle: number
    endAngle: number
    midAngle: number
  }> = []
  
  let currentAngle = 0
  
  data.forEach((item, index) => {
    const angle = (item.value / 100) * 360
    const startAngle = currentAngle
    const endAngle = currentAngle + angle
    const midAngle = (startAngle + endAngle) / 2

    const startRad = (startAngle * Math.PI) / 180
    const endRad = (endAngle * Math.PI) / 180

    const x1 = centerX + radius * Math.cos(startRad)
    const y1 = centerY + radius * Math.sin(startRad)
    const x2 = centerX + radius * Math.cos(endRad)
    const y2 = centerY + radius * Math.sin(endRad)

    const ix1 = centerX + innerRadius * Math.cos(startRad)
    const iy1 = centerY + innerRadius * Math.sin(startRad)
    const ix2 = centerX + innerRadius * Math.cos(endRad)
    const iy2 = centerY + innerRadius * Math.sin(endRad)

    const largeArcFlag = angle > 180 ? 1 : 0

    const pathData = [
      `M ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      `L ${ix2} ${iy2}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${ix1} ${iy1}`,
      'Z'
    ].join(' ')

    paths.push({
      path: pathData,
      color: item.color,
      startAngle,
      endAngle,
      midAngle
    })

    currentAngle = endAngle
  })

  const totalPercent = data.reduce((sum, d) => sum + (Number.isFinite(d.value) ? d.value : 0), 0)

  return (
    <div className="select-none relative">
      {/* Tooltip */}
      {hoveredIndex !== null && tooltipPosition && (
        <div 
          className="fixed px-3 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-md shadow-2xl whitespace-nowrap z-[10000] pointer-events-none"
          style={{ 
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translate(-50%, -100%)',
            marginTop: '-4px'
          }}
        >
          {data[hoveredIndex].name}
          <div 
            className="absolute top-full left-1/2"
            style={{ transform: 'translateX(-50%)' }}
          >
            <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
          </div>
        </div>
      )}
      <svg
        ref={svgRef}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        className="cursor-default"
      >
        {/* Draw segments */}
        {paths.map((pathData, index) => (
          <g key={index}>
            <path
              d={pathData.path}
              fill={pathData.color}
              stroke="#fff"
              strokeWidth={2}
              className="transition-opacity duration-200"
              style={{ opacity: dragging === index || dragging === null ? 1 : 0.7 }}
            />
            
            {/* Percentage label */}
            {data[index].value > 5 && (() => {
              const textX = centerX + ((radius + innerRadius) / 2) * Math.cos((pathData.midAngle * Math.PI) / 180)
              const textY = centerY + ((radius + innerRadius) / 2) * Math.sin((pathData.midAngle * Math.PI) / 180)
              return (
                <g>
                  {/* Transparent hit area for better hover detection */}
                  <circle
                    cx={textX}
                    cy={textY}
                    r={20}
                    fill="transparent"
                    className="pointer-events-auto cursor-help"
                    onMouseEnter={(e) => {
                      const rect = svgRef.current?.getBoundingClientRect()
                      if (rect) {
                        setHoveredIndex(index)
                        setTooltipPosition({
                          x: rect.left + textX,
                          y: rect.top + textY - 10
                        })
                      }
                    }}
                    onMouseLeave={() => {
                      setHoveredIndex(null)
                      setTooltipPosition(null)
                    }}
                  />
                  <text
                    x={textX}
                    y={textY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="fill-white font-semibold pointer-events-none"
                    style={{ fontSize: '16px', textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}
                  >
                    {data[index].value.toFixed(2)}%
                  </text>
                </g>
              )
            })()}
          </g>
        ))}

        {/* Draggable handles on boundaries */}
        {showHandles && paths.map((pathData, index) => {
          const angle = pathData.endAngle
          const rad = (angle * Math.PI) / 180
          const handleRadius = (radius + innerRadius) / 2
          const hx = centerX + handleRadius * Math.cos(rad)
          const hy = centerY + handleRadius * Math.sin(rad)
          const isDraggingThis = dragging === index

          return (
            <g key={`handle-${index}`}>
              {/* Large transparent hit area */}
              <circle
                cx={hx}
                cy={hy}
                r={25}
                fill="transparent"
                className="cursor-grab active:cursor-grabbing"
                onMouseDown={handleMouseDown(index)}
              />
              
              {/* Outer ring */}
              <circle
                cx={hx}
                cy={hy}
                r={isDraggingThis ? 14 : 11}
                fill="white"
                stroke={isDraggingThis ? "#3b82f6" : "#d1d5db"}
                strokeWidth={isDraggingThis ? 3 : 2}
                className="pointer-events-none transition-all duration-200"
                style={{
                  filter: isDraggingThis 
                    ? 'drop-shadow(0 4px 12px rgba(59,130,246,0.5))' 
                    : 'drop-shadow(0 2px 6px rgba(0,0,0,0.15))'
                }}
              />
              
              {/* Inner circle */}
              <circle
                cx={hx}
                cy={hy}
                r={isDraggingThis ? 6 : 5}
                fill={isDraggingThis ? "#3b82f6" : "#9ca3af"}
                className="pointer-events-none transition-all duration-200"
              />
              
              {/* Tiny center dot */}
              <circle
                cx={hx}
                cy={hy}
                r={2}
                fill="white"
                className="pointer-events-none"
              />
            </g>
          )
        })}
        
      </svg>
    </div>
  )
}

